/**
 * Converts between the server's WebAuthn JSON (go-webauthn: binary fields as
 * base64url strings) and the browser's ArrayBuffer-based credential API.
 */

type Json = Record<string, unknown>;

export function base64urlToBuffer(s: string): ArrayBuffer {
  const b64 = s.replace(/-/g, '+').replace(/_/g, '/');
  const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
  const bin = atob(padded);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out.buffer;
}

export function bufferToBase64url(buf: ArrayBuffer | ArrayBufferView): string {
  const bytes =
    buf instanceof ArrayBuffer
      ? new Uint8Array(buf)
      : new Uint8Array(buf.buffer, buf.byteOffset, buf.byteLength);
  let bin = '';
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function decodeDescriptors(list: unknown): PublicKeyCredentialDescriptor[] | undefined {
  if (!Array.isArray(list)) return undefined;
  return list.map((d: Json) => ({
    ...(d as object),
    id: base64urlToBuffer(String(d.id)),
  })) as PublicKeyCredentialDescriptor[];
}

/** Server request options (sign-in) → navigator.credentials.get options. */
export function decodeRequestOptions(publicKey: Json): PublicKeyCredentialRequestOptions {
  return {
    ...(publicKey as object),
    challenge: base64urlToBuffer(String(publicKey.challenge)),
    allowCredentials: decodeDescriptors(publicKey.allowCredentials),
  } as PublicKeyCredentialRequestOptions;
}

/** Server creation options (registration) → navigator.credentials.create options. */
export function decodeCreationOptions(publicKey: Json): PublicKeyCredentialCreationOptions {
  const user = publicKey.user as Json;
  return {
    ...(publicKey as object),
    challenge: base64urlToBuffer(String(publicKey.challenge)),
    user: { ...(user as object), id: base64urlToBuffer(String(user.id)) },
    excludeCredentials: decodeDescriptors(publicKey.excludeCredentials),
  } as PublicKeyCredentialCreationOptions;
}

/** A browser credential → the JSON body the finish endpoints take. */
export function encodeCredential(cred: PublicKeyCredential): Json {
  const r = cred.response as AuthenticatorResponse &
    Partial<AuthenticatorAssertionResponse & AuthenticatorAttestationResponse>;
  const response: Json = { clientDataJSON: bufferToBase64url(r.clientDataJSON) };
  if (r.attestationObject) response.attestationObject = bufferToBase64url(r.attestationObject);
  if (r.authenticatorData) response.authenticatorData = bufferToBase64url(r.authenticatorData);
  if (r.signature) response.signature = bufferToBase64url(r.signature);
  if (r.userHandle) response.userHandle = bufferToBase64url(r.userHandle);
  if (typeof r.getTransports === 'function') response.transports = r.getTransports();
  return { id: cred.id, rawId: bufferToBase64url(cred.rawId), type: cred.type, response };
}

/** True when the browser has the WebAuthn credential API. */
export function passkeysSupported(): boolean {
  return typeof window !== 'undefined' && !!window.PublicKeyCredential && !!navigator.credentials;
}

/** Runs a passkey sign-in ceremony with the server's begin options. */
export async function getPasskey(publicKey: Json): Promise<Json> {
  const cred = await navigator.credentials.get({ publicKey: decodeRequestOptions(publicKey) });
  if (!cred) throw new Error('No passkey was chosen.');
  return encodeCredential(cred as PublicKeyCredential);
}

/** Runs a passkey registration ceremony with the server's begin options. */
export async function createPasskey(publicKey: Json): Promise<Json> {
  const cred = await navigator.credentials.create({ publicKey: decodeCreationOptions(publicKey) });
  if (!cred) throw new Error('No passkey was created.');
  return encodeCredential(cred as PublicKeyCredential);
}
